import React from 'react';
import { Drawer, Button, Tag, Divider, Typography, Alert, Collapse } from 'antd';
import { GlobalOutlined, LinkedinOutlined } from '@ant-design/icons';
import { FilteredOpportunity, LLMRating, FilterReason } from '../../types';
import { renderBulletList } from '../../utils/renderUtils';
import css from './FilteredOpportunityDrawer.module.scss';

const { Title, Text, Paragraph } = Typography;

interface FilteredOpportunityDrawerProps {
  opportunity: FilteredOpportunity | null;
  open: boolean;
  onClose: () => void;
}

const ratingConfig: Record<LLMRating, { label: string; color: string }> = {
  perfect_fit: { label: 'Perfect Fit', color: '#52c41a' },
  strong_alignment: { label: 'Strong Alignment', color: '#73d13d' },
  potential_candidate: { label: 'Potential Candidate', color: '#faad14' },
  partial_fit: { label: 'Partial Fit', color: '#fa8c16' },
  misalignment: { label: 'Misalignment', color: '#ff4d4f' },
};

// Shown when the ad's own sentence didn't survive grading: as the explanation
// itself when there is nothing else, and above the fit rationale when there is.
const MISSING_QUOTE = "We didn't keep the sentence we relied on.";

const filterReasonConfig: Record<FilterReason, { label: string; color: string }> = {
  low_similarity: { label: 'Low Similarity', color: 'blue' },
  low_llm_rating: { label: 'Low Rating', color: 'orange' },
  no_contacts: { label: 'No Contacts', color: 'red' },
  manual_intervention: { label: 'Manual Review', color: 'purple' },
  // Honest copy (design §13): the user's own minimum-match setting did this.
  below_user_threshold: { label: 'Filtered by your minimum match setting', color: 'geekblue' },
  not_eligible: { label: 'Not eligible', color: 'volcano' },
};

const FilteredOpportunityDrawer: React.FC<FilteredOpportunityDrawerProps> = ({
  opportunity,
  open,
  onClose,
}) => {
  if (!opportunity) return null;

  const rating = opportunity.llmRating ? ratingConfig[opportunity.llmRating] : null;
  const filterReason = filterReasonConfig[opportunity.reason];
  const isNoContacts = opportunity.reason === 'no_contacts';
  const isNotEligible = opportunity.reason === 'not_eligible';
  // The ad's own sentence, read from the quote column and nowhere else. The API
  // stores `eligibility_quote or as_text()`, so `llmExplanation` is that sentence
  // only while a quote exists and the MUTUAL-FIT rationale when it doesn't —
  // reading it here would put the drawer one API change away from passing a fit
  // rationale off as the ad's words again.
  const quotedSentence = opportunity.eligibilityQuote?.trim() ?? '';
  // What is left in that case. Trimmed like the quote: the API's `or` keeps a
  // blank-but-not-empty string, and renderBulletList only tests falsiness, so an
  // untrimmed one would render a heading over an empty paragraph.
  const fitRationale = quotedSentence ? '' : opportunity.llmExplanation?.trim() ?? '';
  // The title follows what the section actually holds. With neither sentence it
  // stays on the exclusion question and says below that we didn't keep the answer.
  const notEligibleTitle = fitRationale ? 'Fit Assessment' : "Why you can't take this role";
  // The banner may only assert what the ad itself said (design §"Eligibility grade":
  // an ad is excluded for being hybrid/on-site, or for a residence requirement it
  // states while saying nothing about the workplace type).
  const notEligibleLede =
    opportunity.workplaceType === 'remote'
      ? "This role is remote but the posting requires something your remote search says you don't have"
      : opportunity.workplaceType === 'hybrid' || opportunity.workplaceType === 'on_site'
        ? "This role isn't fully remote"
        : "This posting doesn't match your remote search";

  return (
    <Drawer
      title={null}
      placement="right"
      width="min(560px, 94vw)"
      onClose={onClose}
      open={open}
      className={css.filteredDrawer}
      footer={
        <div className={css.footer}>
          <Button onClick={onClose}>Close</Button>
        </div>
      }
    >
      {/* Header */}
      <div className={css.header}>
        <div className={css.titleRow}>
          <Title level={4} className={css.companyName}>
            {opportunity.company.name}
          </Title>
        </div>
        {opportunity.summary?.jobTitle && (
          <Text className={css.jobTitle}>{opportunity.summary.jobTitle}</Text>
        )}
        <div className={css.tags}>
          {rating && <Tag color={rating.color}>{rating.label}</Tag>}
          <Tag color={filterReason.color}>{filterReason.label}</Tag>
          <span className={css.similarity}>
            {Math.round(opportunity.similarityScore * 100)}% match
          </span>
        </div>
      </div>

      {isNoContacts ? (
        <Alert
          type="warning"
          showIcon
          className={css.calloutBanner}
          message="This opportunity was filtered because no suitable contacts were found to reach out to, not because of the fit assessment below."
        />
      ) : isNotEligible ? (
        <Alert
          type="warning"
          showIcon
          className={css.calloutBanner}
          message={`${notEligibleLede}${
            quotedSentence ? ' — the sentence we relied on is below.' : '.'
          } If we read it wrong, the role is still open to you to apply for yourself.`}
        />
      ) : (
        <Alert
          type="info"
          showIcon
          className={css.calloutBanner}
          message="This assessment is based on your uploaded resume. If you believe this opportunity is a good fit, consider creating a new search with an updated resume that highlights relevant skills and experience for this role."
        />
      )}

      <Divider />

      {/* Explanation section — title varies by filter reason; for not_eligible
          it varies again with whether the posting's own sentence survived. */}
      <section className={css.section}>
        <Title level={5}>
          {isNoContacts
            ? 'Fit Assessment'
            : isNotEligible
              ? notEligibleTitle
              : 'Why It Was Filtered'}
        </Title>
        {isNotEligible ? (
          <>
            {/* The rationale answers a different question than the banner asked,
                so say what is missing before showing it. */}
            {fitRationale && <Paragraph>{MISSING_QUOTE}</Paragraph>}
            {/* The ad's own sentence is a quotation, not a bullet list: the
                bullet renderer drops any line that is not a bullet, and a
                sentence split across lines would lose the half that qualifies
                it. Shown whole, line breaks kept. */}
            {quotedSentence ? (
              <blockquote className={css.adQuote}>{quotedSentence}</blockquote>
            ) : (
              renderBulletList(fitRationale, MISSING_QUOTE)
            )}
          </>
        ) : (
          renderBulletList(opportunity.llmExplanation, 'No detailed explanation available.')
        )}
      </section>

      <Divider />

      <Collapse
        ghost
        className={css.detailsCollapse}
        items={[
          ...(opportunity.summary
            ? [
                {
                  key: 'summary',
                  label: 'Job Summary',
                  children: (
                    <section className={css.section}>
                      <Paragraph>{opportunity.summary.generalOverview}</Paragraph>

                      {opportunity.summary.keyResponsibilities && (
                        <div className={css.listSection}>
                          <Text strong>Key Responsibilities:</Text>
                          <ul>
                            {opportunity.summary.keyResponsibilities.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {opportunity.summary.requiredQualifications && (
                        <div className={css.listSection}>
                          <Text strong>Required Qualifications:</Text>
                          <ul>
                            {opportunity.summary.requiredQualifications.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </section>
                  ),
                },
              ]
            : []),
          {
            key: 'company',
            label: `About ${opportunity.company.name}`,
            children: (
              <section className={css.section}>
                <Paragraph>{opportunity.company.description}</Paragraph>
                <div className={css.links}>
                  {opportunity.company.websiteUrl && (
                    <a href={opportunity.company.websiteUrl} target="_blank" rel="noopener noreferrer">
                      <GlobalOutlined /> Website
                    </a>
                  )}
                  {opportunity.company.linkedinUrl && (
                    <a href={opportunity.company.linkedinUrl} target="_blank" rel="noopener noreferrer">
                      <LinkedinOutlined /> LinkedIn
                    </a>
                  )}
                </div>
              </section>
            ),
          },
        ]}
      />
    </Drawer>
  );
};

export default FilteredOpportunityDrawer;
